import React, { useEffect, useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown } = FiIcons;

const NavDropdown = ({ label, items, mobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef(null);
  const itemRefs = useRef([]);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openMenu = () => {
    clearCloseTimer();
    setIsOpen(true);
  };

  const closeMenu = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setIsOpen(false), 150);
  };

  const toggleMenu = () => {
    clearCloseTimer();
    setIsOpen((open) => !open);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(`[data-dropdown="${label}"]`)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      clearCloseTimer();
    };
  }, [label]);

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMenu();
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openMenu();
      window.setTimeout(() => itemRefs.current[0]?.focus(), 0);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  const handleMenuKeyDown = (event, index) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      itemRefs.current[(index + 1) % items.length]?.focus();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      itemRefs.current[(index - 1 + items.length) % items.length]?.focus();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <div
      data-dropdown={label}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={(event) => {
          event.stopPropagation();
          toggleMenu();
        }}
        onKeyDown={handleTriggerKeyDown}
        className="flex items-center gap-1 hover:text-brass transition-colors"
      >
        {label}
        <SafeIcon icon={FiChevronDown} size={14} />
      </button>

      {isOpen && (
        <div
          role="menu"
          onMouseEnter={openMenu}
          onMouseLeave={closeMenu}
          className={
            mobile
              ? 'static mt-2 w-full rounded-2xl border border-line bg-cream p-2 shadow-lg'
              : 'absolute left-1/2 top-full z-50 mt-4 w-64 -translate-x-1/2 rounded-2xl border border-line bg-cream p-2 shadow-[0_16px_35px_rgba(15,27,45,0.14)] animate-dropdown-in'
          }
        >
          {items.map((item, index) => (
            <React.Fragment key={item.label}>
              {item.divider && <div className="my-2 border-t border-line" />}
              <a
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
                href={item.href}
                role="menuitem"
                tabIndex={isOpen ? 0 : -1}
                onKeyDown={(event) => handleMenuKeyDown(event, index)}
                className={
                  item.more
                    ? 'block rounded-xl px-3 py-2 text-xs font-semibold tracking-wide text-brass hover:bg-warm-cream'
                    : 'block rounded-xl px-3 py-2.5 text-sm font-semibold text-navy hover:bg-warm-cream transition-colors'
                }
              >
                {item.label}
              </a>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default NavDropdown;